import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  AssetSummary,
  GetAssetsByIdsResponse,
  GetProjectsByIdsResponse,
  GetSitesByIdsResponse,
  GetUsersByIdsResponse,
  DataAcquisition,
  ProjectSummary,
  SiteSummary,
  UserSummary,
} from "@aeriosiq/types";

export const enrichmentKeys = {
  sites: (ids: string[]) =>
    ["sites", "by-ids", ids.slice().sort().join(",")] as const,
  assets: (ids: string[]) =>
    ["assets", "by-ids", ids.slice().sort().join(",")] as const,
  projects: (ids: string[]) =>
    ["projects", "by-ids", ids.slice().sort().join(",")] as const,
  users: (ids: string[]) =>
    ["users", "by-ids", ids.slice().sort().join(",")] as const,
};

const STALE_TIME = 5 * 60 * 1000; // 5 min — names rarely change.

function uniqueIds(values: readonly (string | null)[]): string[] {
  const set = new Set<string>();
  for (const v of values) {
    if (v) set.add(v);
  }
  return [...set];
}

export function useSitesByIds(ids: string[]) {
  const api = useApi();
  return useQuery<GetSitesByIdsResponse>({
    queryKey: enrichmentKeys.sites(ids),
    enabled: ids.length > 0,
    staleTime: STALE_TIME,
    queryFn: async () => {
      const response = await api.get<GetSitesByIdsResponse>("/sites", {
        params: { ids: ids.join(",") },
      });
      return response.data;
    },
  });
}

export function useAssetsByIds(ids: string[]) {
  const api = useApi();
  return useQuery<GetAssetsByIdsResponse>({
    queryKey: enrichmentKeys.assets(ids),
    enabled: ids.length > 0,
    staleTime: STALE_TIME,
    queryFn: async () => {
      const response = await api.get<GetAssetsByIdsResponse>("/assets", {
        params: { ids: ids.join(",") },
      });
      return response.data;
    },
  });
}

export interface InspectionEnrichment {
  sitesById: Map<string, SiteSummary>;
  assetsById: Map<string, AssetSummary>;
  isLoading: boolean;
  isError: boolean;
}

export function indexById<T extends { id: string }>(
  items: readonly T[] | undefined,
): Map<string, T> {
  const out = new Map<string, T>();
  if (!items) return out;
  for (const item of items) out.set(item.id, item);
  return out;
}

export function collectSiteIds(items: readonly DataAcquisition[]): string[] {
  return uniqueIds(items.map((i) => i.siteId));
}

export function collectAssetIds(items: readonly DataAcquisition[]): string[] {
  return uniqueIds(items.map((i) => i.assetId));
}

export function formatSite(
  site: SiteSummary | undefined,
  fallback: string | null,
): string {
  if (site) return site.name;
  return fallback ?? "—";
}

/**
 * Asset column format per user feedback (2026-05-08, second iteration):
 *
 *   `${manufacturerAbbreviation} ${identifier ?? code} (${internalParkNumber})`
 *
 * Drop the parens entirely when `internalParkNumber` is null. Drop the
 * leading abbreviation + space when `manufacturerAbbreviation` is null.
 * Falls back to the platform `asset_code` when `identifier` is null
 * (older monolith imports never carried the operator turbine identifier).
 *
 *   "E 1010349 (And-Alb 02)"   ← all three present
 *   "E 1010349"                ← internalParkNumber null
 *   "1010349 (And-Alb 02)"     ← manufacturerAbbreviation null
 *   "10517"                    ← only code
 *   <UUID>                     ← last-resort fallback when asset missing
 */
export function formatAsset(
  asset: AssetSummary | undefined,
  fallback: string | null,
): string {
  if (!asset) return fallback ?? "—";
  const id = asset.identifier ?? asset.code;
  const prefix = asset.manufacturerAbbreviation
    ? `${asset.manufacturerAbbreviation} `
    : "";
  const suffix = asset.internalParkNumber
    ? ` (${asset.internalParkNumber})`
    : "";
  return `${prefix}${id}${suffix}`;
}

// -- Stage 9: project + user enrichment ----------------------------------

export function useProjectsByIds(ids: string[]) {
  const api = useApi();
  return useQuery<GetProjectsByIdsResponse>({
    queryKey: enrichmentKeys.projects(ids),
    enabled: ids.length > 0,
    staleTime: STALE_TIME,
    queryFn: async () => {
      const response = await api.get<GetProjectsByIdsResponse>("/projects", {
        params: { ids: ids.join(",") },
      });
      return response.data;
    },
  });
}

export function useUsersByIds(ids: string[]) {
  const api = useApi();
  return useQuery<GetUsersByIdsResponse>({
    queryKey: enrichmentKeys.users(ids),
    enabled: ids.length > 0,
    staleTime: STALE_TIME,
    queryFn: async () => {
      const response = await api.get<GetUsersByIdsResponse>("/users", {
        params: { ids: ids.join(",") },
      });
      return response.data;
    },
  });
}

export function collectProjectIds(items: readonly DataAcquisition[]): string[] {
  return uniqueIds(items.map((i) => i.projectId));
}

export function collectUserIds(items: readonly DataAcquisition[]): string[] {
  // Pilot + assignee both come from auth_db.public.users — one query covers both.
  return uniqueIds([
    ...items.map((i) => i.pilotUserId),
    ...items.map((i) => i.assignedTo),
  ]);
}

export function formatProject(
  project: ProjectSummary | undefined,
  fallback: string | null,
): string {
  if (project) return project.name;
  return fallback ?? "—";
}

export function formatUser(
  user: UserSummary | undefined,
  fallback: string | null,
): string {
  if (user) return user.displayName;
  return fallback ?? "—";
}

/** Date formatter shared by Inspected On / Planned For.
 * Returns "—" for nullish or unparseable input so the grid stays tidy. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}
